import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { useAuthStore } from '../stores/authStore'
import useThemeStore from '../stores/themeStore'
import useChallengeStore from '../hooks/useChallenge'
import {
  User,
  Mail,
  Calendar,
  Award,
  Save,
  Building2,
  Shield,
  AlertTriangle,
  RefreshCw,
  Globe,
  Key,
  CheckCircle,
  AlertCircle,
  Lock,
  Trophy,
  Eye,
  EyeOff,
  School,
  CreditCard,
  GraduationCap,
  Send,
  Clock,
  XCircle,
  UserPlus,
  X,
  Settings,
  BookOpen,
  BarChart3,
  MessageSquare,
  Loader2,
  Video,
  CalendarDays,
  Check
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import OrganizationSwitcher from '../components/OrganizationSwitcher'
import FamilyMemberManagement from '../components/family/FamilyMemberManagement'
import StrategyLevelCard, { StrategyLevelCardEmpty } from '../components/StrategyLevelCard'
import CommunicationPreferences from '../components/compliance/CommunicationPreferences'
import DataExport from '../components/compliance/DataExport'
import AccountDeletion from '../components/compliance/AccountDeletion'
import AIAdvisorSettings from '../components/settings/AIAdvisorSettings'
import PasskeyManagement from '../components/security/PasskeyManagement'
import BillingTab from '../components/billing/BillingTab'
import PasswordInput from '../components/auth/PasswordInput'
import ConfirmDialog from '../components/ConfirmDialog'
import useConfirmDialog from '../hooks/useConfirmDialog'
import SchoolSelector from '../components/profile/SchoolSelector'
import InviteCoachModal from '../components/profile/InviteCoachModal'
import StrategySelector from '../components/profile/StrategySelector'
import { Avatar, AvatarEditor, BioEditor, SocialHandlesEditor } from '../components/profile'
import BadgeDisplay from '../components/badges/BadgeDisplay'
import availableStrategiesService from '../services/availableStrategiesService'
import coachService from '../services/coachService'
import api from '../services/api'
import featureAnnouncementService from '../services/featureAnnouncementService'
import toast from 'react-hot-toast'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import { getChallengeSessionPath } from '../utils/navigation'
import { createLogger } from '../utils/logger'

const logger = createLogger('Profile')

const Profile = () => {
  const {
    user,
    updateUser,
    strategyLevels,
    fetchStrategyLevels,
    checkAuth,
    fetchMyCoaches: refreshMyCoaches
  } = useAuthStore()
  const { theme, setTheme } = useThemeStore()
  const { currentChallenge, createAndStartChallenge } = useChallengeStore()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  // Feature flags
  const { enabled: coachApplicationsEnabled } = useFeatureFlag('coach_applications')
  const { enabled: darkModeEnabled } = useFeatureFlag('dark_mode')

  const isGuardianAccount = user?.accountType === 'guardian'

  // Initialize activeTab from URL hash or default to 'profile'
  const getTabFromHash = () => {
    const hash = location.hash.replace('#', '')
    // Guardians don't have access to the progress or achievements tabs
    const validTabs = isGuardianAccount
      ? ['profile', 'organization', 'billing', 'security', 'privacy']
      : ['profile', 'organization', 'billing', 'progress', 'achievements', 'security', 'privacy']
    return validTabs.includes(hash) ? hash : 'profile'
  }

  const [activeTab, setActiveTab] = useState(getTabFromHash())
  const [isLoading, setIsLoading] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [strategyLevelsLoading, setStrategyLevelsLoading] = useState(true)
  const [availableStrategies, setAvailableStrategies] = useState([])
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    publicName: '',
    schoolId: null,
    preferences: {
      showRationale: true,
      theme: 'light',
      notifications: true,
      communityNotifications: true
    }
  })

  // Security tab state
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [passwordError, setPasswordError] = useState('')
  const [isResendingVerification, setIsResendingVerification] = useState(false)
  const [showOnLeaderboard, setShowOnLeaderboard] = useState(true)
  const [isUpdatingLeaderboardVisibility, setIsUpdatingLeaderboardVisibility] = useState(false)

  // Coach application state
  const [coachApplication, setCoachApplication] = useState(null)
  const [coachApplicationLoading, setCoachApplicationLoading] = useState(false)
  const [showCoachApplyModal, setShowCoachApplyModal] = useState(false)
  const [coachApplyForm, setCoachApplyForm] = useState({ message: '', experience: '' })
  const [coachApplyLoading, setCoachApplyLoading] = useState(false)

  // My Coaches state
  const [myCoaches, setMyCoaches] = useState([])
  const [myCoachesLoading, setMyCoachesLoading] = useState(false)
  const [coachInvitations, setCoachInvitations] = useState([]) // Received invitations (coach wants to coach user)
  const [sentInvitations, setSentInvitations] = useState([]) // Sent invitations (user invited coach, waiting for response)
  const [showInviteCoachModal, setShowInviteCoachModal] = useState(false)
  const [removingCoach, setRemovingCoach] = useState(null)
  const [selectedCoachForPermissions, setSelectedCoachForPermissions] = useState(null)
  const [showCoachPermissionsModal, setShowCoachPermissionsModal] = useState(false)

  // Confirm dialog hook
  const {
    isOpen: confirmDialogOpen,
    config: confirmConfig,
    closeDialog,
    handleConfirm,
    confirmDialog
  } = useConfirmDialog()
  const [coachPermissions, setCoachPermissions] = useState({
    canViewProgress: true,
    canManageChallenges: true,
    canViewAnalytics: true,
    canSendMessages: true
  })
  const [savingPermissions, setSavingPermissions] = useState(false)

  // Social profile state
  const [socialFormData, setSocialFormData] = useState({
    bio: '',
    showBio: true,
    avatarType: 'dicebear',
    avatarSeed: null,
    avatarStyle: 'bottts',
    avatarData: null,
    socialHandles: null,
    showSocialHandles: false,
    // Coach contact fields
    coachZoomLink: '',
    coachCalendlyLink: '',
    coachContactNote: ''
  })
  const [isEditingAvatar, setIsEditingAvatar] = useState(false)

  // Check if user is a minor (under 18) - minors cannot access GDPR self-service functions
  const isMinor = useMemo(() => {
    if (!user?.dateOfBirth) return false
    const birthDate = new Date(user.dateOfBirth)
    const today = new Date()
    const age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000))
    return age < 18
  }, [user?.dateOfBirth])

  // Sync activeTab with URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '')
    if (hash && hash !== activeTab) {
      // Guardians don't have access to the progress or achievements tabs
      const validTabs = isGuardianAccount
        ? ['profile', 'organization', 'billing', 'security', 'privacy']
        : ['profile', 'organization', 'billing', 'progress', 'achievements', 'security', 'privacy']
      if (validTabs.includes(hash)) {
        setActiveTab(hash)
      }
    }
  }, [location.hash, isGuardianAccount])

  // Update URL hash when activeTab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId)
    navigate(`/profile#${tabId}`, { replace: true })
  }

  // Initialize form data from user - wrapped in useCallback to avoid stale closures
  const initializeFormData = () => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        publicName: user.publicName || '',
        schoolId: user.schoolId || null,
        preferences: {
          showRationale: user.profile?.showRationale ?? true,
          theme: theme,
          notifications: user.profile?.notifications ?? true,
          communityNotifications: user.profile?.communityNotifications ?? true
        }
      })

      // Initialize social profile data from user
      setSocialFormData({
        bio: user.bio || '',
        showBio: user.showBio ?? true,
        avatarType: user.avatarType || 'dicebear',
        avatarSeed: user.avatarSeed || null,
        avatarStyle: user.avatarStyle || 'bottts',
        avatarData: user.avatarData || null,
        socialHandles: user.socialHandles || null,
        showSocialHandles: user.showSocialHandles ?? false,
        // Coach contact fields
        coachZoomLink: user.coachZoomLink || '',
        coachCalendlyLink: user.coachCalendlyLink || '',
        coachContactNote: user.coachContactNote || ''
      })

      // Initialize leaderboard visibility from user data
      setShowOnLeaderboard(user.showOnLeaderboard ?? true)
    }
  }

  // Initialize on mount - ensures data loads on PWA navigation
  useEffect(() => {
    initializeFormData()
    if (user) {
      loadStrategyLevels()
    }
    // Run on mount only
  }, [])

  // Re-initialize when user or theme changes
  useEffect(() => {
    initializeFormData()
    if (user) {
      loadStrategyLevels()
    }
  }, [user, theme])

  // Refresh strategy levels when navigating back to profile
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        loadStrategyLevels()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [user])

  // Fetch coach application status
  useEffect(() => {
    const fetchCoachApplication = async () => {
      if (!user || user.isCoach) return
      try {
        setCoachApplicationLoading(true)
        const response = await fetch('/api/user/coach-application', { credentials: 'include' })
        const data = await response.json()
        if (data.success) {
          setCoachApplication(data.application)
        }
      } catch (error) {
        logger.error('Error fetching coach application:', error)
      } finally {
        setCoachApplicationLoading(false)
      }
    }
    fetchCoachApplication()
  }, [user])

  // Handle coach application submission
  const handleCoachApply = async () => {
    if (!coachApplyForm.message.trim()) {
      toast.error('Please tell us why you want to become a coach')
      return
    }
    try {
      setCoachApplyLoading(true)
      const response = await fetch('/api/user/apply-coach', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(coachApplyForm)
      })
      const data = await response.json()
      if (data.success) {
        toast.success("Application submitted! You'll be notified once reviewed.")
        setCoachApplication(data.application)
        setShowCoachApplyModal(false)
        setCoachApplyForm({ message: '', experience: '' })
      } else {
        toast.error(data.error || 'Failed to submit application')
      }
    } catch (error) {
      logger.error('Error applying to coach:', error)
      toast.error('Failed to submit application')
    } finally {
      setCoachApplyLoading(false)
    }
  }

  // Handle coach application withdrawal
  const handleCoachWithdraw = async () => {
    const confirmed = await confirmDialog({
      title: 'Withdraw Application',
      message: 'Are you sure you want to withdraw your coach application? You can reapply later.',
      confirmText: 'Withdraw',
      cancelText: 'Cancel'
    })
    if (!confirmed) return
    try {
      const response = await fetch('/api/user/coach-application', {
        method: 'DELETE',
        credentials: 'include'
      })
      const data = await response.json()
      if (data.success) {
        toast.success('Application withdrawn')
        setCoachApplication(null)
      } else {
        toast.error(data.error || 'Failed to withdraw application')
      }
    } catch (error) {
      logger.error('Error withdrawing application:', error)
      toast.error('Failed to withdraw application')
    }
  }

  // Handle accepting admin coach invitation
  const handleAcceptCoachInvitation = async () => {
    if (!coachApplication?.invitationToken) {
      toast.error('Invalid invitation')
      return
    }
    try {
      setCoachApplicationLoading(true)
      const response = await api.post(
        `/user/accept-coach-invitation/${coachApplication.invitationToken}`
      )
      if (response.data.success) {
        toast.success('Congratulations! You are now a coach!')
        setCoachApplication(null)
        // Refresh user data to get updated isCoach status
        checkAuth()
      }
    } catch (error) {
      logger.error('Error accepting invitation:', error)
      toast.error(error.response?.data?.error || 'Failed to accept invitation')
    } finally {
      setCoachApplicationLoading(false)
    }
  }

  // Handle declining admin coach invitation
  const handleDeclineCoachInvitation = async () => {
    if (!coachApplication?.invitationToken) {
      toast.error('Invalid invitation')
      return
    }
    const confirmed = await confirmDialog({
      title: 'Decline Invitation',
      message: 'Are you sure you want to decline this coach invitation?',
      confirmText: 'Decline',
      cancelText: 'Cancel'
    })
    if (!confirmed) return
    try {
      setCoachApplicationLoading(true)
      const response = await api.post(
        `/user/decline-coach-invitation/${coachApplication.invitationToken}`
      )
      if (response.data.success) {
        toast.success('Invitation declined')
        setCoachApplication(null)
      }
    } catch (error) {
      logger.error('Error declining invitation:', error)
      toast.error(error.response?.data?.error || 'Failed to decline invitation')
    } finally {
      setCoachApplicationLoading(false)
    }
  }

  // Fetch user's coaches
  const fetchMyCoaches = async () => {
    try {
      setMyCoachesLoading(true)
      const [coachesRes, invitationsRes] = await Promise.all([
        api.get('/user/coaches'),
        api.get('/user/coach-invitations')
      ])
      if (coachesRes.data.success) {
        setMyCoaches(coachesRes.data.coaches)
      }
      if (invitationsRes.data.success) {
        // received: invitations from coaches that user can accept/decline
        // sent: invitations user sent to coaches, waiting for response
        setCoachInvitations(invitationsRes.data.received || [])
        setSentInvitations(invitationsRes.data.sent || [])
      }
    } catch (error) {
      logger.error('Error fetching coaches:', error)
    } finally {
      setMyCoachesLoading(false)
    }
  }

  useEffect(() => {
    if (user) {
      fetchMyCoaches()
    }
  }, [user])

  // Handle removing a coach
  const handleRemoveCoach = async (coachId) => {
    const confirmed = await confirmDialog({
      title: 'Remove Coach',
      message:
        'Are you sure you want to remove this coach? They will no longer be able to view your progress or send you messages.',
      confirmText: 'Remove',
      cancelText: 'Cancel'
    })
    if (!confirmed) return
    try {
      setRemovingCoach(coachId)
      const response = await api.delete(`/user/coaches/${coachId}`)
      if (response.data.success) {
        toast.success('Coach removed')
        fetchMyCoaches()
      }
    } catch (error) {
      logger.error('Error removing coach:', error)
      toast.error(error.response?.data?.message || 'Failed to remove coach')
    } finally {
      setRemovingCoach(null)
    }
  }

  // Handle opening permissions modal for a coach
  const handleOpenCoachPermissions = (coach) => {
    setSelectedCoachForPermissions(coach)
    setCoachPermissions(
      coach.permissions || {
        canViewProgress: true,
        canManageChallenges: true,
        canViewAnalytics: true,
        canSendMessages: true
      }
    )
    setShowCoachPermissionsModal(true)
  }

  // Handle saving coach permissions
  const handleSaveCoachPermissions = async () => {
    if (!selectedCoachForPermissions) return

    // Ensure at least one permission is enabled
    const anyEnabled = Object.values(coachPermissions).some((v) => v)
    if (!anyEnabled) {
      toast.error('At least one permission must be enabled')
      return
    }

    setSavingPermissions(true)
    try {
      const response = await coachService.updateMyCoachPermissions(
        selectedCoachForPermissions.id,
        coachPermissions
      )
      if (response.success) {
        toast.success('Permissions updated')
        // Update local state
        setMyCoaches((prev) =>
          prev.map((c) =>
            c.id === selectedCoachForPermissions.id ? { ...c, permissions: coachPermissions } : c
          )
        )
        setShowCoachPermissionsModal(false)
        setSelectedCoachForPermissions(null)
      }
    } catch (error) {
      logger.error('Error saving permissions:', error)
      toast.error(error.response?.data?.message || 'Failed to save permissions')
    } finally {
      setSavingPermissions(false)
    }
  }

  // Handle accepting coach invitation
  const handleAcceptInvitation = async (relationshipId) => {
    try {
      const response = await api.post(`/user/coach-invitations/${relationshipId}/accept`)
      if (response.data.success) {
        toast.success('Coach invitation accepted!')
        fetchMyCoaches() // Update local state
        refreshMyCoaches() // Update authStore (for sidebar)
      }
    } catch (error) {
      logger.error('Error accepting invitation:', error)
      toast.error(error.response?.data?.message || 'Failed to accept invitation')
    }
  }

  // Handle declining coach invitation
  const handleDeclineInvitation = async (relationshipId) => {
    try {
      const response = await api.post(`/user/coach-invitations/${relationshipId}/decline`)
      if (response.data.success) {
        toast.success('Invitation declined')
        fetchMyCoaches()
      }
    } catch (error) {
      logger.error('Error declining invitation:', error)
      toast.error(error.response?.data?.message || 'Failed to decline invitation')
    }
  }

  // Handle canceling a sent invitation
  const handleCancelInvitation = async (relationshipId) => {
    try {
      const response = await api.delete(`/user/coach-invitations/${relationshipId}`)
      if (response.data.success) {
        toast.success('Invitation cancelled')
        fetchMyCoaches()
      }
    } catch (error) {
      logger.error('Error canceling invitation:', error)
      toast.error(error.response?.data?.message || 'Failed to cancel invitation')
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target

    if (name.startsWith('preferences.')) {
      const prefKey = name.split('.')[1]
      setFormData((prev) => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          [prefKey]: type === 'checkbox' ? checked : value
        }
      }))

      // Trigger theme change immediately for theme preference
      if (prefKey === 'theme') {
        setTheme(value)
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const loadStrategyLevels = async () => {
    try {
      setStrategyLevelsLoading(true)
      const [, strategies] = await Promise.all([
        fetchStrategyLevels(),
        availableStrategiesService.getAvailableStrategies()
      ])
      setAvailableStrategies(strategies)
    } catch (error) {
      logger.error('Error loading strategy levels:', error)
    } finally {
      setStrategyLevelsLoading(false)
    }
  }

  const handleSaveProfile = async () => {
    setIsLoading(true)

    try {
      const response = await fetch('/api/auth/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          // Profile fields
          firstName: formData.firstName,
          lastName: formData.lastName,
          publicName: formData.publicName,
          schoolId: formData.schoolId,
          profile: formData.preferences,
          // Social profile fields
          bio: socialFormData.bio,
          showBio: socialFormData.showBio,
          avatarType: socialFormData.avatarType,
          avatarSeed: socialFormData.avatarSeed,
          avatarStyle: socialFormData.avatarStyle,
          avatarData: socialFormData.avatarData,
          socialHandles: socialFormData.socialHandles,
          showSocialHandles: socialFormData.showSocialHandles,
          // Coach contact fields (only sent if user is a coach)
          ...(user?.isCoach && {
            coachZoomLink: socialFormData.coachZoomLink || null,
            coachCalendlyLink: socialFormData.coachCalendlyLink || null,
            coachContactNote: socialFormData.coachContactNote || null
          })
        })
      })

      if (!response.ok) {
        let errorMessage = 'Failed to update profile'

        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorData.message || errorMessage
        } catch {
          if (response.status === 401) {
            errorMessage = 'Your session has expired. Please log in again.'
          } else if (response.status === 403) {
            errorMessage = 'You do not have permission to update this profile.'
          } else if (response.status >= 500) {
            errorMessage = 'Server error. Please try again later.'
          }
        }

        throw new Error(errorMessage)
      }

      const data = await response.json()

      if (!data || !data.user) {
        throw new Error('Invalid response from server')
      }

      updateUser({
        firstName: data.user.firstName,
        lastName: data.user.lastName,
        name: data.user.name,
        publicName: data.user.publicName,
        schoolId: data.user.schoolId,
        profile: data.user.profile,
        bio: data.user.bio,
        showBio: data.user.showBio,
        avatarType: data.user.avatarType,
        avatarSeed: data.user.avatarSeed,
        avatarStyle: data.user.avatarStyle,
        avatarData: data.user.avatarData,
        socialHandles: data.user.socialHandles,
        showSocialHandles: data.user.showSocialHandles,
        // Coach contact fields
        coachZoomLink: data.user.coachZoomLink,
        coachCalendlyLink: data.user.coachCalendlyLink,
        coachContactNote: data.user.coachContactNote
      })

      toast.success('Profile updated successfully!')
    } catch (error) {
      logger.error('Profile update error:', error)
      const errorMessage = error.message || 'Failed to update profile'
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAvatarSave = (avatarData) => {
    setSocialFormData((prev) => ({ ...prev, ...avatarData }))
    setIsEditingAvatar(false)
  }

  const handleRegeneratePublicName = async () => {
    setIsRegenerating(true)

    try {
      const response = await fetch('/api/auth/regenerate-public-name', {
        method: 'POST',
        credentials: 'include'
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to regenerate public name')
      }

      // Update local form state
      setFormData((prev) => ({ ...prev, publicName: data.publicName }))
      // Update global user state
      updateUser({ publicName: data.publicName })
      toast.success('Public name regenerated!')
    } catch (error) {
      logger.error('Regenerate public name error:', error)
      toast.error(error.message || 'Failed to regenerate public name')
    } finally {
      setIsRegenerating(false)
    }
  }

  const handleViewStrategy = (strategyType) => {
    navigate(`/strategy/${strategyType}`)
  }

  const handleStartStrategyChallenge = async (strategyType) => {
    if (currentChallenge) {
      toast.error('You already have an active challenge. Please complete or cancel it first.')
      navigate(getChallengeSessionPath(currentChallenge.id))
      return
    }

    try {
      const challengeData = {
        strategyType: strategyType
      }

      logger.debug('🚀 Starting challenge for strategy:', strategyType)
      const result = await createAndStartChallenge(challengeData)

      if (result.success) {
        toast.success('Challenge started successfully!')
        navigate(getChallengeSessionPath(result.challenge?.id), { replace: true })
      } else {
        logger.error('❌ Failed to start challenge:', result.error)
        toast.error(result.error || 'Failed to start challenge')
      }
    } catch (error) {
      logger.error('Error starting challenge:', error)
      toast.error('Failed to start challenge')
    }
  }

  // Security handlers
  const handlePasswordFormChange = (e) => {
    const { name, value } = e.target
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value
    }))
    setPasswordError('')
  }

  const handleChangePassword = async () => {
    // Validate passwords
    if (
      !passwordForm.currentPassword ||
      !passwordForm.newPassword ||
      !passwordForm.confirmPassword
    ) {
      setPasswordError('All fields are required')
      return
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters')
      return
    }

    if (passwordForm.newPassword === passwordForm.currentPassword) {
      setPasswordError('New password must be different from current password')
      return
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match')
      return
    }

    setIsChangingPassword(true)
    setPasswordError('')

    try {
      const response = await fetch('/api/auth/password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to change password')
      }

      toast.success('Password changed successfully. Please log in with your new password.')
      // Clear auth state and redirect to login
      const { logout, clearAuthData } = useAuthStore.getState()
      try {
        await logout(queryClient)
      } catch (logoutError) {
        logger.error('Logout after password change failed:', logoutError)
        // Force clear auth data even if server call fails
        clearAuthData(queryClient)
      } finally {
        navigate('/login')
      }
    } catch (error) {
      logger.error('Password change error:', error)
      setPasswordError(error.message || 'Failed to change password')
      setIsChangingPassword(false)
    }
  }

  const handleResendVerification = async () => {
    setIsResendingVerification(true)

    try {
      const response = await fetch('/api/auth/send-verification-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ email: user.email })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send verification email')
      }

      toast.success('Verification email sent! Please check your inbox.')
    } catch (error) {
      logger.error('Resend verification error:', error)
      toast.error(error.message || 'Failed to send verification email')
    } finally {
      setIsResendingVerification(false)
    }
  }

  const handleLeaderboardVisibilityChange = async (newValue) => {
    setIsUpdatingLeaderboardVisibility(true)
    const previousValue = showOnLeaderboard

    try {
      // Optimistic update
      setShowOnLeaderboard(newValue)

      const response = await fetch('/api/rankings/visibility', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ showOnLeaderboard: newValue })
      })

      if (!response.ok) {
        throw new Error('Failed to update visibility')
      }

      // Update auth store
      updateUser({ showOnLeaderboard: newValue })
      toast.success(
        newValue ? 'You will now appear on leaderboards' : 'You are now hidden from leaderboards'
      )
    } catch (error) {
      logger.error('Error updating leaderboard visibility:', error)
      // Revert on error
      setShowOnLeaderboard(previousValue)
      toast.error('Failed to update leaderboard visibility')
    } finally {
      setIsUpdatingLeaderboardVisibility(false)
    }
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const tabs = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'organization', name: 'Organization', icon: Building2 },
    { id: 'billing', name: 'Billing', icon: CreditCard },
    // Hide Strategy Progress and Achievements tabs for guardians - they don't have their own progress
    ...(!isGuardianAccount ? [{ id: 'progress', name: 'Strategy Progress', icon: Award }] : []),
    ...(!isGuardianAccount ? [{ id: 'achievements', name: 'Achievements', icon: Trophy }] : []),
    { id: 'security', name: 'Security', icon: Key },
    { id: 'privacy', name: 'Privacy & Data', icon: Shield }
  ]

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
            <Avatar
              type={socialFormData.avatarType}
              seed={socialFormData.avatarSeed}
              style={socialFormData.avatarStyle}
              data={socialFormData.avatarData}
              name={`${user?.firstName || ''} ${user?.lastName || ''}`}
              publicName={user?.publicName}
              size="lg"
              className="flex-shrink-0"
            />
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
                {user.firstName} {user.lastName}
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 truncate">
                {user.email}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          <nav className="flex min-w-max px-4 sm:px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`
                    flex items-center gap-1.5 px-3 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                    ${
                      isActive
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:border-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                </button>
              )
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-4 sm:p-6">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100">
                  Profile & Social
                </h3>
                <div className="flex items-center gap-2">
                  {user?.publicName && (
                    <Link
                      to={`/u/${user.publicName}`}
                      className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                    >
                      <Eye className="w-4 h-4" />
                      View public profile
                    </Link>
                  )}
                </div>
              </div>

              {/* Avatar Section */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <Avatar
                    type={socialFormData.avatarType}
                    seed={socialFormData.avatarSeed}
                    style={socialFormData.avatarStyle}
                    data={socialFormData.avatarData}
                    name={`${user?.firstName || ''} ${user?.lastName || ''}`}
                    publicName={user?.publicName}
                    size="lg"
                    className="ring-4 ring-white dark:ring-gray-700"
                  />
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      Profile Picture
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      Choose initials, generate an avatar, or upload your own
                    </p>
                    <button
                      type="button"
                      onClick={() => setIsEditingAvatar(true)}
                      className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      Change avatar
                    </button>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Personal Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address
                    </label>
                    <div className="flex items-center space-x-2 min-w-0 h-10 px-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                      <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
                        {user.email}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Member Since
                    </label>
                    <div className="flex items-center space-x-2 h-10 px-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                      <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {new Date(user.createdAt || Date.now()).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Public Profile */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Public Profile
                </h4>
                <div className="space-y-4">
                  {/* Public Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        <span>Public Name</span>
                      </div>
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      This name is displayed on leaderboards instead of your real name for privacy.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="publicName"
                        value={formData.publicName}
                        onChange={handleChange}
                        placeholder="Enter a custom name or regenerate"
                        className="input flex-1"
                        maxLength={30}
                      />
                      <button
                        type="button"
                        onClick={handleRegeneratePublicName}
                        disabled={isRegenerating}
                        className="btn-secondary flex items-center gap-2 px-3"
                        title="Generate a new random name"
                      >
                        <RefreshCw className={`w-4 h-4 ${isRegenerating ? 'animate-spin' : ''}`} />
                        <span className="hidden sm:inline">Regenerate</span>
                      </button>
                    </div>
                  </div>

                  {/* School */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <div className="flex items-center gap-2">
                        <School className="w-4 h-4" />
                        <span>Your School</span>
                      </div>
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      Select your school to connect with classmates and track performance.
                    </p>
                    <SchoolSelector
                      value={formData.schoolId}
                      onChange={(schoolId) => setFormData((prev) => ({ ...prev, schoolId }))}
                    />
                  </div>
                </div>
              </div>

              {/* Bio Section */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <BioEditor
                  bio={socialFormData.bio}
                  onChange={(bio) => setSocialFormData((prev) => ({ ...prev, bio }))}
                  showOnProfile={socialFormData.showBio}
                  onToggleVisibility={(show) =>
                    setSocialFormData((prev) => ({ ...prev, showBio: show }))
                  }
                />
              </div>

              {/* Social Handles Section */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <SocialHandlesEditor
                  handles={socialFormData.socialHandles}
                  onChange={(handles) =>
                    setSocialFormData((prev) => ({ ...prev, socialHandles: handles }))
                  }
                  showOnProfile={socialFormData.showSocialHandles}
                  onToggleVisibility={(show) =>
                    setSocialFormData((prev) => ({ ...prev, showSocialHandles: show }))
                  }
                />
              </div>

              {/* Coach Contact Links Section - Only for coaches */}
              {user?.isCoach && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      Coach Contact Links
                    </h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    These links will be displayed on your public profile so students can reach you
                    for coaching sessions.
                  </p>

                  <div className="space-y-4">
                    {/* Zoom Link */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <Video className="w-4 h-4 text-primary-500" />
                        Zoom Meeting Link
                      </label>
                      <input
                        type="url"
                        value={socialFormData.coachZoomLink}
                        onChange={(e) =>
                          setSocialFormData((prev) => ({ ...prev, coachZoomLink: e.target.value }))
                        }
                        placeholder="https://zoom.us/j/your-meeting-id"
                        className="input w-full"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Your Zoom personal meeting link for coaching sessions
                      </p>
                    </div>

                    {/* Calendly Link */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <CalendarDays className="w-4 h-4 text-purple-500" />
                        Scheduling Link
                      </label>
                      <input
                        type="url"
                        value={socialFormData.coachCalendlyLink}
                        onChange={(e) =>
                          setSocialFormData((prev) => ({
                            ...prev,
                            coachCalendlyLink: e.target.value
                          }))
                        }
                        placeholder="https://calendly.com/your-name"
                        className="input w-full"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Calendly or other scheduling link for booking sessions
                      </p>
                    </div>

                    {/* Contact Note */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <MessageSquare className="w-4 h-4 text-green-500" />
                        Contact Note
                      </label>
                      <textarea
                        value={socialFormData.coachContactNote}
                        onChange={(e) =>
                          setSocialFormData((prev) => ({
                            ...prev,
                            coachContactNote: e.target.value
                          }))
                        }
                        placeholder="e.g., Book a free 15-minute intro call!"
                        maxLength={200}
                        rows={2}
                        className="input w-full resize-none"
                      />
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>Short message to encourage students to reach out</span>
                        <span>{socialFormData.coachContactNote?.length || 0}/200</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Preferences Section */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Preferences</h4>
                <div className="space-y-4">
                  <div className="flex items-start sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Show Rationale
                      </h5>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        Display explanations after answering questions
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        name="preferences.showRationale"
                        checked={formData.preferences.showRationale}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  <div className="flex items-start sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Notifications
                      </h5>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        Receive email notifications about test results
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        name="preferences.notifications"
                        checked={formData.preferences.notifications}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  <div className="flex items-start sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Community Notifications
                      </h5>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        Show activity updates from other learners in your community
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        name="preferences.communityNotifications"
                        checked={formData.preferences.communityNotifications}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  {/* Announcement frequency preference */}
                  <div className="flex items-start sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        What's New Updates
                      </h5>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        How often to show new feature announcements on login
                      </p>
                    </div>
                    <select
                      aria-label="Announcement notification frequency"
                      value={user?.announcementFrequency || 'every_login'}
                      onChange={async (e) => {
                        try {
                          await featureAnnouncementService.updateFrequency(e.target.value)
                          updateUser({ announcementFrequency: e.target.value })
                          toast.success('Announcement frequency updated')
                        } catch {
                          toast.error('Failed to update preference')
                        }
                      }}
                      className="input w-auto text-sm flex-shrink-0"
                    >
                      <option value="every_login">Every login</option>
                      <option value="daily">Once a day</option>
                      <option value="weekly">Once a week</option>
                    </select>
                  </div>

                  {/* Weekly progress email preference (opt-in; in-app updates always on) */}
                  <div className="flex items-start sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Weekly Progress Emails
                      </h5>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        Get an encouraging weekly recap of your progress by email. In-app updates
                        stay on either way.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        aria-label="Weekly progress emails"
                        checked={!user?.progressEmailsOptOut}
                        onChange={async (e) => {
                          const optOut = !e.target.checked
                          try {
                            await api.put('/users/me/preferences', { progressEmailsOptOut: optOut })
                            updateUser({ progressEmailsOptOut: optOut })
                            toast.success(
                              optOut
                                ? 'Weekly progress emails turned off'
                                : 'Weekly progress emails turned on'
                            )
                          } catch {
                            toast.error('Failed to update preference')
                          }
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  {darkModeEnabled && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Theme
                      </label>
                      <select
                        name="preferences.theme"
                        value={formData.preferences.theme}
                        onChange={handleChange}
                        className="input w-full"
                      >
                        <option value="light">Light</option>
                        <option value="dark">Dark</option>
                        <option value="auto">Auto</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Challenge Strategy Section */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="mt-0">
                  <StrategySelector
                    currentStrategy={user?.challengeStrategy || 'STANDARD_ADAPTIVE'}
                    onUpdate={(strategy) => {
                      updateUser({ challengeStrategy: strategy })
                    }}
                  />
                </div>
              </div>

              {/* Save Button */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleSaveProfile}
                  disabled={isLoading}
                  className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto"
                >
                  {isLoading ? <LoadingSpinner size="sm" /> : <Save className="w-4 h-4" />}
                  <span>Save Profile</span>
                </button>
              </div>

              {/* Avatar Editor Modal */}
              {isEditingAvatar && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                      Edit Avatar
                    </h3>
                    <AvatarEditor
                      currentType={socialFormData.avatarType}
                      currentSeed={socialFormData.avatarSeed}
                      currentStyle={socialFormData.avatarStyle}
                      currentData={socialFormData.avatarData}
                      name={`${user?.firstName || ''} ${user?.lastName || ''}`}
                      publicName={user?.publicName}
                      onSave={handleAvatarSave}
                      onCancel={() => setIsEditingAvatar(false)}
                    />
                  </div>
                </div>
              )}

              {/* Become a Coach Section - only show if feature flag enabled or user has pending invitation */}
              {!user?.isCoach &&
                !isGuardianAccount &&
                (coachApplicationsEnabled || coachApplication?.applicationType === 'invite') && (
                  <div className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                          Become a Coach
                        </h4>
                        {coachApplicationLoading ? (
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <LoadingSpinner size="xs" />
                            Loading...
                          </div>
                        ) : coachApplication?.status === 'pending' &&
                          coachApplication?.applicationType === 'invite' ? (
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Mail className="w-4 h-4 text-green-500" />
                              <span className="text-sm text-green-700 dark:text-green-300 font-medium">
                                You've been invited to become a coach!
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                              An admin has selected you to join the coaching team.
                              {coachApplication.invitationExpiresAt && (
                                <>
                                  {' '}
                                  This invitation expires on{' '}
                                  {new Date(
                                    coachApplication.invitationExpiresAt
                                  ).toLocaleDateString()}
                                  .
                                </>
                              )}
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={handleAcceptCoachInvitation}
                                disabled={coachApplicationLoading}
                                className="btn btn-sm btn-primary inline-flex items-center gap-2"
                              >
                                <Check className="w-4 h-4" />
                                Accept
                              </button>
                              <button
                                onClick={handleDeclineCoachInvitation}
                                disabled={coachApplicationLoading}
                                className="btn btn-sm btn-secondary text-red-600 dark:text-red-400"
                              >
                                Decline
                              </button>
                            </div>
                          </div>
                        ) : coachApplication?.status === 'pending' ? (
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Clock className="w-4 h-4 text-yellow-500" />
                              <span className="text-sm text-yellow-700 dark:text-yellow-300 font-medium">
                                Application pending review
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                              Your application was submitted on{' '}
                              {new Date(coachApplication.createdAt).toLocaleDateString()}. You'll be
                              notified once it's reviewed.
                            </p>
                            <button
                              onClick={handleCoachWithdraw}
                              className="text-sm text-red-600 dark:text-red-400 hover:underline"
                            >
                              Withdraw application
                            </button>
                          </div>
                        ) : coachApplication?.status === 'declined' ? (
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <XCircle className="w-4 h-4 text-red-500" />
                              <span className="text-sm text-red-700 dark:text-red-300 font-medium">
                                Application declined
                              </span>
                            </div>
                            {coachApplication.declineReason && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                Reason: {coachApplication.declineReason}
                              </p>
                            )}
                            <button
                              onClick={() => setShowCoachApplyModal(true)}
                              className="btn btn-sm btn-primary"
                            >
                              Apply Again
                            </button>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                              Share your expertise and help others learn! Apply to become a coach
                              and manage students.
                            </p>
                            <button
                              onClick={() => setShowCoachApplyModal(true)}
                              className="btn btn-sm btn-primary inline-flex items-center gap-2"
                            >
                              <Send className="w-4 h-4" />
                              Apply Now
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* Coach Application Modal */}
          {showCoachApplyModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Apply to Become a Coach
                    </h3>
                    <button
                      onClick={() => {
                        setShowCoachApplyModal(false)
                        setCoachApplyForm({ message: '', experience: '' })
                      }}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      <XCircle className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Why do you want to become a coach? *
                    </label>
                    <textarea
                      value={coachApplyForm.message}
                      onChange={(e) =>
                        setCoachApplyForm((prev) => ({ ...prev, message: e.target.value }))
                      }
                      placeholder="Tell us about your motivation to help others learn..."
                      rows={4}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Relevant experience (optional)
                    </label>
                    <textarea
                      value={coachApplyForm.experience}
                      onChange={(e) =>
                        setCoachApplyForm((prev) => ({ ...prev, experience: e.target.value }))
                      }
                      placeholder="Describe any teaching, tutoring, or mentoring experience..."
                      rows={3}
                      className="input w-full"
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Your application will be reviewed by our team. You'll be notified via email once
                    a decision is made.
                  </p>
                </div>
                <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
                  <button
                    onClick={() => {
                      setShowCoachApplyModal(false)
                      setCoachApplyForm({ message: '', experience: '' })
                    }}
                    className="btn btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCoachApply}
                    disabled={coachApplyLoading || !coachApplyForm.message.trim()}
                    className="btn btn-primary flex-1 inline-flex items-center justify-center gap-2"
                  >
                    {coachApplyLoading ? (
                      <LoadingSpinner size="xs" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Submit Application
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Organization Tab */}
          {activeTab === 'organization' && (
            <div className="space-y-6">
              {/* Family Member Management - handles org switching internally */}
              <FamilyMemberManagement />

              {/* Non-family organizations (school, personal, etc.) */}
              <OrganizationSwitcher variant="card" showOnlyNonFamily />

              {/* My Coaches Section */}
              <div className="card p-6">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                      <GraduationCap className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        My Coaches
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Coaches who help you on your learning journey
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowInviteCoachModal(true)}
                    className="btn btn-primary btn-sm inline-flex items-center gap-2 flex-shrink-0"
                  >
                    <UserPlus className="w-4 h-4" />
                    Find Coach
                  </button>
                </div>

                {/* Sent Invitations (waiting for coach to accept) */}
                {sentInvitations.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sent Invitations
                    </h4>
                    <div className="space-y-2">
                      {sentInvitations.map((invitation) => (
                        <div
                          key={invitation.id}
                          className="flex items-center justify-between p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <Avatar
                              type={invitation.coach?.avatarType}
                              seed={invitation.coach?.avatarSeed}
                              style={invitation.coach?.avatarStyle}
                              name={`${invitation.coach?.firstName || ''} ${invitation.coach?.lastName || ''}`}
                              publicName={invitation.coach?.publicName}
                              size="sm"
                            />
                            <div>
                              <p className="font-medium text-gray-900 dark:text-gray-100">
                                {invitation.coach?.firstName} {invitation.coach?.lastName}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Invitation sent - awaiting response
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleCancelInvitation(invitation.id)}
                            className="btn btn-sm btn-secondary"
                          >
                            Cancel
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Received Invitations (coach wants to coach user) */}
                {coachInvitations.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Pending Invitations
                    </h4>
                    <div className="space-y-2">
                      {coachInvitations.map((invitation) => (
                        <div
                          key={invitation.id}
                          className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <Avatar
                              type={invitation.coach?.avatarType}
                              seed={invitation.coach?.avatarSeed}
                              style={invitation.coach?.avatarStyle}
                              name={`${invitation.coach?.firstName || ''} ${invitation.coach?.lastName || ''}`}
                              publicName={invitation.coach?.publicName}
                              size="sm"
                            />
                            <div>
                              <p className="font-medium text-gray-900 dark:text-gray-100">
                                {invitation.coach?.firstName} {invitation.coach?.lastName}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                wants to coach you
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleAcceptInvitation(invitation.id)}
                              className="btn btn-sm btn-primary"
                            >
                              Accept
                            </button>
                            <button
                              onClick={() => handleDeclineInvitation(invitation.id)}
                              className="btn btn-sm btn-secondary"
                            >
                              Decline
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Active Coaches */}
                {myCoachesLoading ? (
                  <div className="flex justify-center py-12">
                    <LoadingSpinner />
                  </div>
                ) : myCoaches.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 mt-2 pt-8">
                    <GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p className="mb-1 font-medium text-gray-600 dark:text-gray-300">
                      You don't have any coaches yet
                    </p>
                    <p className="text-sm">Find a coach to help guide your learning</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {myCoaches.map((coach) => (
                      <div
                        key={coach.id}
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar
                            type={coach.avatarType}
                            seed={coach.avatarSeed}
                            style={coach.avatarStyle}
                            name={`${coach.firstName || ''} ${coach.lastName || ''}`}
                            publicName={coach.publicName}
                            size="md"
                          />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {coach.firstName} {coach.lastName}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {coach.email}
                            </p>
                            {coach.coachProfile?.headline && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {coach.coachProfile.headline}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleOpenCoachPermissions(coach)}
                            className="btn btn-sm btn-secondary"
                            title="Manage what this coach can see"
                          >
                            <Settings className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRemoveCoach(coach.id)}
                            disabled={removingCoach === coach.id}
                            className="btn btn-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                            title="Remove coach"
                          >
                            {removingCoach === coach.id ? (
                              <LoadingSpinner size="xs" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && <BillingTab />}

          {/* Strategy Progress Tab - Hidden for guardians */}
          {activeTab === 'progress' && !isGuardianAccount && (
            <div className="space-y-6">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100">
                Strategy Levels & Progress
              </h3>

              {strategyLevelsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    Loading strategy levels...
                  </span>
                </div>
              ) : availableStrategies.length === 0 ? (
                <div className="text-center py-8">
                  <Award className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">No strategies available</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {availableStrategies.map((strategy) => {
                    const strategyType = strategy.strategyType || strategy.type
                    if (!strategyType) {
                      logger.warn('Strategy missing strategyType field:', strategy)
                      return null
                    }

                    const strategyLevel = strategyLevels.find(
                      (level) => level.strategyType === strategyType
                    )

                    if (strategyLevel) {
                      return (
                        <StrategyLevelCard
                          key={strategyType}
                          strategyLevel={strategyLevel}
                          onClick={() => handleViewStrategy(strategyType)}
                          onStart={() => handleStartStrategyChallenge(strategyType)}
                          showDetailedStats={true}
                          publicName={user?.publicName}
                        />
                      )
                    } else {
                      return (
                        <StrategyLevelCardEmpty
                          key={strategyType}
                          strategyType={strategyType}
                          onStart={() => handleStartStrategyChallenge(strategyType)}
                        />
                      )
                    }
                  })}
                </div>
              )}
            </div>
          )}

          {/* Achievements Tab - Hidden for guardians */}
          {activeTab === 'achievements' && !isGuardianAccount && <BadgeDisplay userId={user?.id} />}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100">
                Security Settings
              </h3>

              {/* Email Verification Section */}
              <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-4">
                  <Mail className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Email Verification
                  </h4>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-700 dark:text-gray-300">{user.email}</span>
                    {user.emailVerified ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                        <CheckCircle className="w-3 h-3" />
                        Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                        <AlertCircle className="w-3 h-3" />
                        Not Verified
                      </span>
                    )}
                  </div>

                  {!user.emailVerified && (
                    <button
                      onClick={handleResendVerification}
                      disabled={isResendingVerification}
                      className="btn-secondary flex items-center justify-center gap-2 text-sm"
                    >
                      {isResendingVerification ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      <span>Resend Verification Email</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Change Password Section */}
              <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-4">
                  <Lock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Change Password
                  </h4>
                </div>

                <div className="space-y-4">
                  {passwordError && (
                    <div className="p-3 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                      {passwordError}
                    </div>
                  )}

                  <PasswordInput
                    id="currentPassword"
                    name="currentPassword"
                    label="Current Password"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordFormChange}
                    autoComplete="current-password"
                  />

                  <PasswordInput
                    id="newPassword"
                    name="newPassword"
                    label="New Password"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordFormChange}
                    showStrengthMeter={true}
                    autoComplete="new-password"
                    error={
                      passwordForm.newPassword &&
                      passwordForm.currentPassword &&
                      passwordForm.newPassword === passwordForm.currentPassword
                        ? 'New password must be different from current password'
                        : undefined
                    }
                  />

                  <PasswordInput
                    id="confirmPassword"
                    name="confirmPassword"
                    label="Confirm New Password"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordFormChange}
                    autoComplete="new-password"
                    error={
                      passwordForm.confirmPassword &&
                      passwordForm.newPassword &&
                      passwordForm.confirmPassword !== passwordForm.newPassword
                        ? 'Passwords do not match'
                        : undefined
                    }
                  />

                  <div className="pt-2">
                    <button
                      onClick={handleChangePassword}
                      disabled={
                        isChangingPassword ||
                        !passwordForm.currentPassword ||
                        !passwordForm.newPassword ||
                        !passwordForm.confirmPassword ||
                        passwordForm.newPassword === passwordForm.currentPassword ||
                        passwordForm.newPassword !== passwordForm.confirmPassword
                      }
                      className="btn-primary flex items-center justify-center gap-2"
                    >
                      {isChangingPassword ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Key className="w-4 h-4" />
                      )}
                      <span>Change Password</span>
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Note: You will be logged out after changing your password.
                  </p>
                </div>
              </div>

              {/* Passkey Management Section */}
              <PasskeyManagement />
            </div>
          )}

          {/* Support Tickets link (always visible in security tab) */}
          {activeTab === 'security' && (
            <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Support Tickets
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    View and track your feedback and bug reports
                  </p>
                </div>
                <button
                  onClick={() => navigate('/my-feedback')}
                  className="px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                >
                  View Tickets
                </button>
              </div>
            </div>
          )}

          {/* Privacy & Data Tab */}
          {activeTab === 'privacy' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Privacy & Data Settings
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Manage your privacy preferences, data export, and account
                </p>
              </div>

              {/* Minor Account Notice */}
              {isMinor && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
                        Guardian-Managed Account
                      </h4>
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        Because you are under 18, certain privacy functions such as data export and
                        account deletion must be managed by your parent or guardian. This is in
                        compliance with child protection laws (COPPA). Please ask your guardian to
                        submit any privacy requests on your behalf.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Privacy Notice - Show different message for minors */}
              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-semibold text-primary-900 dark:text-primary-100 mb-1">
                      Your Privacy Rights
                    </h4>
                    <p className="text-sm text-primary-800 dark:text-primary-200">
                      We comply with GDPR, CCPA, and COPPA regulations.{' '}
                      {isMinor ? (
                        <>
                          Your guardian has full control over your data and can manage privacy
                          settings on your behalf. For more information, see our{' '}
                          <Link
                            to="/legal/privacy"
                            target="_blank"
                            className="underline hover:text-primary-600 dark:hover:text-primary-300"
                          >
                            Privacy Policy
                          </Link>
                          .
                        </>
                      ) : (
                        <>
                          You have full control over your data. For more information, see our{' '}
                          <Link
                            to="/legal/privacy"
                            target="_blank"
                            className="underline hover:text-primary-600 dark:hover:text-primary-300"
                          >
                            Privacy Policy
                          </Link>{' '}
                          or{' '}
                          <Link
                            to="/privacy-request"
                            className="underline hover:text-primary-600 dark:hover:text-primary-300"
                          >
                            submit a privacy request
                          </Link>
                          .
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Leaderboard Visibility - Available to all non-guardians */}
              {!isGuardianAccount && (
                <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-4">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Leaderboard Visibility
                    </h4>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                        Show my rankings on public leaderboards
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {showOnLeaderboard
                          ? 'Your ranking and public name are visible to other users on leaderboards.'
                          : "You can still view rankings, but you won't appear on leaderboards."}
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {showOnLeaderboard ? (
                          <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                            <Eye className="w-4 h-4" />
                            Visible
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-gray-500">
                            <EyeOff className="w-4 h-4" />
                            Hidden
                          </span>
                        )}
                      </span>

                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showOnLeaderboard}
                          onChange={(e) => handleLeaderboardVisibilityChange(e.target.checked)}
                          disabled={isUpdatingLeaderboardVisibility}
                          className="sr-only peer"
                        />
                        <div
                          className={`w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 ${isUpdatingLeaderboardVisibility ? 'opacity-50 cursor-not-allowed' : ''}`}
                        ></div>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Communication Preferences - Available to all */}
              <CommunicationPreferences />

              {/* AI Study Advisor opt-in (self-hides when the feature flag is off) */}
              <AIAdvisorSettings />

              {/* Data Export - Only for adults */}
              {!isMinor && <DataExport />}

              {/* Account Deletion - Only for adults */}
              {!isMinor && <AccountDeletion />}

              {/* Help Section */}
              <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Need Help?
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {isMinor
                    ? 'If you have questions about your privacy or data, please ask your parent or guardian to contact our privacy team.'
                    : 'If you have questions about your privacy or data, our privacy team is here to help.'}
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="mailto:privacy@ebucis.com"
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                  >
                    Email Privacy Team
                  </a>
                  {!isMinor && (
                    <Link
                      to="/privacy-request"
                      className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                    >
                      Submit Privacy Request
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invite Coach Modal */}
      <InviteCoachModal
        isOpen={showInviteCoachModal}
        onClose={() => setShowInviteCoachModal(false)}
        organizations={user?.organizations?.map((org) => ({ id: org.id, name: org.name })) || []}
        onInviteSent={fetchMyCoaches}
      />

      {/* Coach Permissions Modal */}
      {showCoachPermissionsModal && selectedCoachForPermissions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Coach Permissions
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedCoachForPermissions.firstName} {selectedCoachForPermissions.lastName}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowCoachPermissionsModal(false)
                    setSelectedCoachForPermissions(null)
                  }}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Permission toggles */}
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Control what this coach can access for your account:
              </p>

              {/* View Progress */}
              <div className="flex items-start gap-4 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <Eye className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <label className="font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
                      View Progress
                    </label>
                    <button
                      role="switch"
                      aria-checked={coachPermissions.canViewProgress}
                      onClick={() =>
                        setCoachPermissions((prev) => ({
                          ...prev,
                          canViewProgress: !prev.canViewProgress
                        }))
                      }
                      disabled={savingPermissions}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                        coachPermissions.canViewProgress
                          ? 'bg-primary-600'
                          : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${coachPermissions.canViewProgress ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    See challenge history, scores, and learning progress
                  </p>
                </div>
              </div>

              {/* Manage Challenges */}
              <div className="flex items-start gap-4 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <label className="font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
                      Manage Challenges
                    </label>
                    <button
                      role="switch"
                      aria-checked={coachPermissions.canManageChallenges}
                      onClick={() =>
                        setCoachPermissions((prev) => ({
                          ...prev,
                          canManageChallenges: !prev.canManageChallenges
                        }))
                      }
                      disabled={savingPermissions}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                        coachPermissions.canManageChallenges
                          ? 'bg-primary-600'
                          : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${coachPermissions.canManageChallenges ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    Assign challenges and view detailed question responses
                  </p>
                </div>
              </div>

              {/* View Analytics */}
              <div className="flex items-start gap-4 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <label className="font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
                      View Analytics
                    </label>
                    <button
                      role="switch"
                      aria-checked={coachPermissions.canViewAnalytics}
                      onClick={() =>
                        setCoachPermissions((prev) => ({
                          ...prev,
                          canViewAnalytics: !prev.canViewAnalytics
                        }))
                      }
                      disabled={savingPermissions}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                        coachPermissions.canViewAnalytics
                          ? 'bg-primary-600'
                          : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${coachPermissions.canViewAnalytics ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    Access detailed performance analytics and trends
                  </p>
                </div>
              </div>

              {/* Send Messages */}
              <div className="flex items-start gap-4 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <label className="font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
                      Send Messages
                    </label>
                    <button
                      role="switch"
                      aria-checked={coachPermissions.canSendMessages}
                      onClick={() =>
                        setCoachPermissions((prev) => ({
                          ...prev,
                          canSendMessages: !prev.canSendMessages
                        }))
                      }
                      disabled={savingPermissions}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                        coachPermissions.canSendMessages
                          ? 'bg-primary-600'
                          : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${coachPermissions.canSendMessages ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    Communicate directly with you
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowCoachPermissionsModal(false)
                  setSelectedCoachForPermissions(null)
                }}
                className="btn btn-secondary flex-1"
                disabled={savingPermissions}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveCoachPermissions}
                className="btn btn-primary flex-1 inline-flex items-center justify-center gap-2"
                disabled={savingPermissions}
              >
                {savingPermissions ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialogOpen}
        onClose={closeDialog}
        onConfirm={handleConfirm}
        {...confirmConfig}
      />
    </div>
  )
}

export default Profile
